/**
 * The fees a collector adds to an invoice at collection time, as the desk holds them
 * before they become the API's `line_items`.
 *
 * Shared by the Collection Desk and the fee builder it renders — the builder appears in
 * two places (the invoice pane, and a single billed month in the grid) and both drive
 * the same draft rows.
 */

import type { FeeType } from '../../services/feeHeadApi';
import type { TransportRouteRecord } from '../../services/transportRouteApi';

export const round2 = (n: number): number => Math.round(n * 100) / 100;

export function formatCurrency(n: number): string {
    return '₹' + n.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

/** A route's configured fare, when it has one. The API sends it as a decimal string. */
export function routeFare(route: TransportRouteRecord): number | undefined {
    const raw = route.default_monthly_fee;
    if (raw === null || raw === undefined || raw === '') return undefined;
    const fare = typeof raw === 'number' ? raw : parseFloat(raw);
    return Number.isFinite(fare) && fare > 0 ? fare : undefined;
}

/* ── Additional-fee builder ──────────────────────────────────────────────
   Catalog heads are ticked off a checkbox list — one row per head, each with its
   own amount & discount box — and a ticked row is backed by an `existing` draft
   line here. The list can't express a head that isn't in the catalog yet, nor a
   transport fare, so those are still added as free-form cards below it: a `new`
   line (head entered by name, saved to the catalog) or a `transport` line. */
export type LineItemType = 'fee_head' | 'transport';

/**
 * How a fee applies across the months being collected.
 *
 *  - `split`     — one charge, divided evenly over the months (₹300 over 3 → ₹100 each).
 *  - `per_month` — the charge lands on every month (₹300 over 3 → ₹900). What a transport
 *                  fare wants: a monthly fare across 3 months is 3 × fare, never fare ÷ 3.
 *  - `once`      — the whole charge on the newest month alone. What a genuine one-off wants.
 *
 * Required by the API whenever `months` is sent, and rejected when `invoice_id` is —
 * a single invoice has nothing to distribute across.
 */
export type FeeDistribution = 'split' | 'per_month' | 'once';

export const DISTRIBUTION_OPTIONS: { value: FeeDistribution; label: string; hint: string }[] = [
    { value: 'split', label: 'Split', hint: 'divided evenly across the months' },
    { value: 'per_month', label: 'Per month', hint: 'charged on every month' },
    { value: 'once', label: 'Once', hint: 'charged on the last month only' },
];

export interface DraftLineItem {
    key: string;
    type: LineItemType;
    /* fee_head */
    feeHeadMode: 'existing' | 'new';
    feeHeadId: string;
    feeHeadName: string;
    feeHeadType: FeeType;
    /* transport: the route being charged. Sent alongside the fare so the payment is
       tied to a route; the server takes `amount` over the route's default. */
    routeId: string;
    /* shared: gross amount (fee_head) / fare (transport) */
    amount: number | '';
    isOptional: boolean;
    /* inline one-off discount — off the fee amount, or off the transport fare */
    discountEnabled: boolean;
    discountAmount: number | '';
    discountName: string;
    /* Only meaningful when collecting for months; ignored on a single-invoice payment. */
    distribution: FeeDistribution;
    /* transport: the desk put this row there, not the collector — the student's record names
       a route nothing else is billing. Only drives the row's "why am I here" note. */
    prefilled: boolean;
}

let lineKeySeq = 0;
export function makeLineItem(type: LineItemType = 'fee_head'): DraftLineItem {
    lineKeySeq += 1;
    return {
        key: `li-${Date.now()}-${lineKeySeq}`,
        type,
        feeHeadMode: 'existing',
        feeHeadId: '',
        feeHeadName: '',
        feeHeadType: 'other',
        routeId: '',
        amount: '',
        isOptional: false,
        discountEnabled: false,
        discountAmount: '',
        discountName: '',
        // A fare is inherently monthly; a fee head is a lump the collector is spreading.
        distribution: type === 'transport' ? 'per_month' : 'split',
        prefilled: false,
    };
}

/** Gross (pre-discount) amount a draft line charges. */
export function lineItemGross(it: DraftLineItem): number {
    return typeof it.amount === 'number' && it.amount > 0 ? it.amount : 0;
}

/** Inline discount on a draft line, as a positive amount. */
export function lineItemDiscount(it: DraftLineItem): number {
    return it.discountEnabled && typeof it.discountAmount === 'number' && it.discountAmount > 0 ? it.discountAmount : 0;
}

/** Net (billable) amount a draft line contributes: gross − discount, floored at 0. */
export function lineItemNet(it: DraftLineItem): number {
    return Math.max(0, round2(lineItemGross(it) - lineItemDiscount(it)));
}

/** Net total of a set of draft lines, as a single invoice bills them. */
export function lineItemsNet(items: DraftLineItem[]): number {
    return round2(items.reduce((sum, it) => sum + lineItemNet(it), 0));
}

/**
 * What a draft line actually adds when collecting across `monthCount` months.
 *
 * `split` and `once` bill the charge once however many months are selected — only
 * `per_month` multiplies. This is the figure the API wants in
 * `expected_line_items_total`: the *pre-split* net across the whole payment.
 */
export function lineItemNetOverMonths(it: DraftLineItem, monthCount: number): number {
    const net = lineItemNet(it);
    const months = Math.max(1, monthCount);
    return it.distribution === 'per_month' ? round2(net * months) : net;
}

export function lineItemsNetOverMonths(items: DraftLineItem[], monthCount: number): number {
    return round2(items.reduce((sum, it) => sum + lineItemNetOverMonths(it, monthCount), 0));
}

/**
 * The per-month gross a `split` row lands on each month, for display only — the server
 * computes the real split (integer paise, remainder to the oldest months) and it, not
 * this, is authoritative. Shown so the collector can see ₹300 becoming ₹100 × 3.
 */
export function splitPreview(amount: number, monthCount: number): number[] {
    const n = Math.max(1, monthCount);
    const paise = Math.round(amount * 100);
    const base = Math.floor(paise / n);
    const rem = paise % n;
    return Array.from({ length: n }, (_, i) => (base + (i < rem ? 1 : 0)) / 100);
}
